import { useCallback, useEffect, useRef, useState } from 'react';

import {
  BookmarkIcon,
  MicIcon,
  PaperclipIcon,
  SmileIcon,
  StickyNoteIcon,
  XIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { EmojiPicker } from '@/components/organisms/EmojiPicker/EmojiPicker';
import { Button } from '@/components/ui/button';
import { useDeskId } from '@/hooks/useDeskId';
import { cn } from '@/lib/utils';
import { useComments } from '@/modules/humanChat/hooks/useComments';
import { selectSessionId } from '@/redux/session/selectors';
import type { Comment } from '@/shared/types/comments';
import { setCursorAtEnd } from '@/util/dom';
import { LENGTH_2048 } from '@/util/validation/constants';

type NoteBoxProps = { comment?: Comment | null; onClose: () => void };

export const NoteBox = ({ comment, onClose }: NoteBoxProps) => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);
  const { createComment, updateComment } = useComments(deskId, sessionId);

  const isEditMode = !!comment;
  const inputRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<Range | null>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const [text, setText] = useState(comment?.text ?? '');
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

  useEffect(() => {
    const value = comment?.text ?? '';
    setText(value);
    if (inputRef.current) {
      inputRef.current.innerText = value;
      setCursorAtEnd(inputRef.current);
      inputRef.current.focus();
    }
  }, [comment]);

  const handleInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) {
      return;
    }

    const raw = el.innerText ?? '';
    if (raw.length > LENGTH_2048) {
      el.innerText = raw.slice(0, LENGTH_2048);
      setCursorAtEnd(el);
    }
    setText(el.innerText);
  }, []);

  const handleEmojiButtonClick = useCallback(() => {
    if (emojiPickerOpen) {
      setEmojiPickerOpen(false);
      return;
    }
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    }
    setEmojiPickerOpen(true);
  }, [emojiPickerOpen]);

  const handleEmojiClick = useCallback(
    ({ emoji }: { emoji: string }) => {
      const input = inputRef.current;
      const sel = window.getSelection();
      if (!input || !sel) {
        return;
      }

      input.focus();

      let range: Range;
      if (savedSelectionRef.current) {
        sel.removeAllRanges();
        sel.addRange(savedSelectionRef.current);
        range = savedSelectionRef.current;
      } else if (sel.rangeCount > 0) {
        range = sel.getRangeAt(0);
      } else {
        range = document.createRange();
        range.selectNodeContents(input);
        range.collapse(false);
        sel.addRange(range);
      }

      const textNode = document.createTextNode(emoji);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);

      handleInput();
      savedSelectionRef.current = null;
      setEmojiPickerOpen(false);
    },
    [handleInput]
  );

  const handleSubmit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) {
      if (!isEditMode) {
        inputRef.current?.focus();
      }
      return;
    }

    if (isEditMode) {
      if (trimmed !== comment.text) {
        updateComment({ commentId: comment.comment_id, text: trimmed });
      }
      onClose();
    } else {
      createComment(trimmed);
      setText('');
      if (inputRef.current) {
        inputRef.current.innerText = '';
      }
    }
  }, [text, isEditMode, comment, createComment, updateComment, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit, onClose]
  );

  const isDisabled = isEditMode ? !text.trim() || text.trim() === comment.text : false;

  return (
    <div className="flex flex-col px-4 pb-6">
      <div className="mx-auto flex w-full max-w-3xl flex-col">
        <div className="flex w-80 items-center gap-1.5 rounded-t-xl border border-b-0 border-stroke bg-bg-warning-soft px-3 py-1.5">
          <StickyNoteIcon size={16} className="shrink-0 text-foreground-muted" />
          <span className="flex-1 text-xs leading-4 font-medium text-foreground">
            {isEditMode ? t('notes.edit_note') : t('notes.note_visible_to_agents')}
          </span>
          <Button
            variant="ghost-neutral"
            size="icon-xss"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <XIcon size={16} />
          </Button>
        </div>

        <section className="relative flex min-h-35 flex-col rounded-xl rounded-tl-none border border-stroke bg-background text-sm leading-5 font-normal">
          {emojiPickerOpen && (
            <div className="absolute bottom-full left-0 z-10 mb-2 h-50 w-full">
              <EmojiPicker
                onClickAway={(e) => {
                  if (emojiButtonRef.current?.contains(e.target as Node)) {
                    return;
                  }
                  setEmojiPickerOpen(false);
                }}
                onEmojiClick={handleEmojiClick}
                width="100%"
                height={200}
              />
            </div>
          )}
          <div className="flex flex-1 flex-col pt-3 pr-2.5 pb-2.5 pl-3">
            <div
              ref={inputRef}
              className="max-h-70 flex-1 overflow-hidden overflow-y-auto border-0 text-sm leading-6 break-words focus:border-0 focus:outline-0 [&[contenteditable]:empty::before]:text-foreground-muted [&[contenteditable]:empty::before]:content-[attr(data-placeholder)]"
              role="textbox"
              contentEditable
              tabIndex={0}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              data-placeholder={t('notes.placeholder')}
            />

            <div className="flex items-end justify-between">
              <div className="flex self-end">
                <InfoTooltip content={t('notes.unavailable_in_note_mode')} showArrow>
                  <span>
                    <Button
                      type="button"
                      variant="ghost-neutral"
                      size="icon-sm"
                      disabled
                      aria-label={t('chatBox.quick_responses')}
                    >
                      <BookmarkIcon size={20} />
                    </Button>
                  </span>
                </InfoTooltip>

                <InfoTooltip content={t('notes.close_note')} showArrow>
                  <Button
                    type="button"
                    variant="ghost-neutral"
                    size="icon-sm"
                    onClick={onClose}
                    aria-label={t('notes.close_note')}
                    className="border border-informational bg-bg-info-soft text-informational hover:bg-bg-info-soft hover:text-informational [&_svg]:text-informational"
                  >
                    <StickyNoteIcon size={20} />
                  </Button>
                </InfoTooltip>

                <InfoTooltip content={t('notes.unavailable_in_note_mode')} showArrow>
                  <span>
                    <Button
                      type="button"
                      variant="ghost-neutral"
                      size="icon-sm"
                      disabled
                      aria-label={t('chatBox.attach_file')}
                    >
                      <PaperclipIcon size={20} />
                    </Button>
                  </span>
                </InfoTooltip>

                <InfoTooltip content={t('emojiPicker.emoji_panel')} showArrow>
                  <Button
                    ref={emojiButtonRef}
                    type="button"
                    variant="ghost-neutral"
                    size="icon-sm"
                    onClick={handleEmojiButtonClick}
                    aria-label={t('emojiPicker.emoji_panel')}
                    className={cn(
                      emojiPickerOpen &&
                        'border border-informational bg-bg-info-soft text-informational hover:bg-bg-info-soft hover:text-informational [&_svg]:text-informational'
                    )}
                  >
                    <SmileIcon size={20} />
                  </Button>
                </InfoTooltip>
              </div>

              <div className="flex items-center gap-2 self-end">
                <InfoTooltip content={t('notes.unavailable_in_note_mode')} showArrow>
                  <span>
                    <Button
                      type="button"
                      variant="ghost-neutral"
                      size="icon-sm"
                      disabled
                      aria-label={t('chatbox.microphone')}
                    >
                      <MicIcon size={18} />
                    </Button>
                  </span>
                </InfoTooltip>
                <Button size="sm" disabled={isDisabled} onClick={handleSubmit}>
                  {isEditMode ? t('common.update') : t('common.add')}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
